import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CompaniesApi, CompanyLetterheadDto } from '@buspro/web/apis';
import { injectQuery, injectQueryClient } from '@tanstack/angular-query-experimental';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { lastValueFrom } from 'rxjs';

const KEY = ['company', 'letterhead'];

/**
 * What the company's reports print at the top: the address, and the colour used for rules and
 * labels. The colour comes from the logo unless someone chooses one.
 */
@Component({
  selector: 'tenant-letterhead-settings',
  standalone: true,
  imports: [CommonModule, FormsModule, NzButtonModule, NzInputModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="space-y-8">
      <div class="grid grid-cols-4 gap-y-2">
        <div class="font-semibold">Company address</div>
        @if (letterhead.isPending()) {
          <div class="col-span-3"><div class="skeleton h-4 w-48"></div></div>
        } @else if (!editing()) {
          <div class="col-span-2 text-base-content" data-test="address-text">
            @if (addressText(); as text) {
              {{ text }}
            } @else {
              <span class="text-base-content/60">Not set. Reports print no address until you add one.</span>
            }
          </div>
          <div class="flex justify-end">
            <button type="button" class="text-xl text-base-content/60 hover:text-primary" aria-label="Edit address" (click)="startEditing()">
              <span class="fe fe-edit-2"></span>
            </button>
          </div>
        } @else {
          <div class="col-span-3 space-y-2">
            <input nz-input maxlength="160" placeholder="Street and building, e.g. Plot 12 Kampala Road" aria-label="Address line 1" [(ngModel)]="line1" />
            <input nz-input maxlength="160" placeholder="Area or P.O. Box (optional)" aria-label="Address line 2" [(ngModel)]="line2" />
            <input nz-input maxlength="80" placeholder="Town or city" aria-label="Town or city" [(ngModel)]="city" />
            <div class="flex gap-2 pt-1">
              <button nz-button nzType="primary" [nzLoading]="saving()" (click)="saveAddress()">Save address</button>
              <button nz-button (click)="editing.set(false)">Cancel</button>
            </div>
          </div>
        }
      </div>

      <div class="grid grid-cols-4 gap-y-2">
        <div>
          <div class="font-semibold">Report colour</div>
          <div class="text-xs text-base-content/60">Rules and labels on PDFs</div>
        </div>
        <div class="col-span-3 flex flex-wrap items-center gap-3">
          <input
            type="color"
            class="h-9 w-12 cursor-pointer rounded border border-base-300 bg-base-100"
            aria-label="Report colour"
            [value]="letterhead.data()?.accentColor ?? '#1f4e8c'"
            (change)="chooseColour($any($event.target).value)"
          />
          <span class="text-sm text-base-content/70">
            @if (letterhead.data()?.accentColorManual) {
              Chosen by your company.
              <button type="button" class="link link-primary" (click)="useLogoColour()">Use the logo's colour instead</button>
            } @else if (letterhead.data()?.accentColor) {
              Taken from your logo.
            } @else {
              Taken from your logo the first time a report is made.
            }
          </span>
        </div>
      </div>
    </div>
  `,
})
export class LetterheadSettingsComponent {
  private api = inject(CompaniesApi);
  private message = inject(NzMessageService);
  private queryClient = injectQueryClient();

  letterhead = injectQuery(() => ({
    queryKey: KEY,
    queryFn: () => lastValueFrom(this.api.companyControllerGetLetterhead()),
  }));

  editing = signal(false);
  saving = signal(false);
  line1 = '';
  line2 = '';
  city = '';

  addressText(): string {
    const data = this.letterhead.data();
    return data ? [data.addressLine1, data.addressLine2, data.city].filter(Boolean).join(', ') : '';
  }

  startEditing(): void {
    const data = this.letterhead.data();
    this.line1 = data?.addressLine1 ?? '';
    this.line2 = data?.addressLine2 ?? '';
    this.city = data?.city ?? '';
    this.editing.set(true);
  }

  async saveAddress(): Promise<void> {
    if (this.saving()) return;
    this.saving.set(true);
    try {
      // The id in the path is not read: the server always writes the caller's own company.
      await lastValueFrom(
        this.api.companyControllerUpdateCompanyAddress({ id: 'mine', body: { addressLine1: this.line1, addressLine2: this.line2, city: this.city } }),
      );
      await this.queryClient.invalidateQueries({ queryKey: KEY });
      this.editing.set(false);
      this.message.success('Address saved. New reports will print it.');
    } catch (error) {
      this.message.error((error as { error?: { detail?: string } })?.error?.detail || 'The address could not be saved.');
    } finally {
      this.saving.set(false);
    }
  }

  chooseColour(colour: string): Promise<void> {
    return this.setColour(colour, 'Report colour saved.');
  }

  useLogoColour(): Promise<void> {
    return this.setColour(null, 'Reports will use your logo’s colour again.');
  }

  private async setColour(accentColor: string | null, done: string): Promise<void> {
    try {
      const updated: CompanyLetterheadDto = await lastValueFrom(this.api.companyControllerUpdateAccent({ body: { accentColor } }));
      this.queryClient.setQueryData(KEY, updated);
      this.message.success(done);
    } catch (error) {
      this.message.error((error as { error?: { detail?: string } })?.error?.detail || 'The colour could not be saved.');
    }
  }
}
